import { Box, keyframes } from '@mui/material';
import { motion } from 'motion/react';
import { useState } from 'react';

const bounce = keyframes`
  0%, 80%, 100% {
    transform: scale(0);
    opacity: 0.4;
  }
  40% {
    transform: scale(1);
    opacity: 1;
  }
`;

const breathe = keyframes`
  0%, 100% {
    transform: scale(1);
  }
  50% {
    transform: scale(1.02);
  }
`;

const containerVariants = {
  exit: {
    opacity: 0,
    backdropFilter: 'blur(20px)',
    backgroundColor: 'transparent',
    transition: {
      duration: 0.5,
      ease: 'easeOut',
      staggerChildren: 0.05,
      staggerDirection: -1,
      backdropFilter: { duration: 0.3, ease: 'easeOut' },
      backgroundColor: { duration: 0.4, ease: 'easeOut' },
    },
  },
};

const dotsVariants = {
  exit: { opacity: 0, transition: { duration: 0.15, ease: 'easeOut' } },
};

const logoVariants = {
  exit: {
    opacity: 0,
    scale: 0.96,
    transition: {
      opacity: { duration: 0.25, ease: 'easeOut' },
      scale: { type: 'spring', bounce: 0, visualDuration: 0.25 },
    },
  },
};

export default function AppLoader({
  logoSrc = '/favicon.svg',
}: {
  logoSrc?: string;
}) {
  const [logoLoaded, setLogoLoaded] = useState(false);

  return (
    <Box
      component={motion.div}
      variants={containerVariants}
      initial={{ opacity: 1 }}
      exit="exit"
      role="status"
      aria-label="Loading"
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        height: '100vh',
        width: '100vw',
        position: 'fixed',
        inset: 0,
        zIndex: (theme) => theme.zIndex.drawer + 1,
        bgcolor: (theme) => theme.palette.background.default,
      }}
    >
      <Box
        component={motion.div}
        variants={logoVariants}
        sx={{
          mb: 3,
          willChange: 'transform, opacity',
          display: 'flex',
          justifyContent: 'center',
        }}
      >
        <Box
          sx={{
            animation: `${breathe} 4s ease-in-out 1s infinite`,
            display: 'flex',
            justifyContent: 'center',
          }}
        >
          <motion.img
            src={logoSrc}
            alt=""
            aria-hidden="true"
            fetchPriority="high"
            onLoad={() => setLogoLoaded(true)}
            initial={{ opacity: 0, scale: 0.85 }}
            animate={
              logoLoaded
                ? { opacity: 1, scale: 1 }
                : { opacity: 0, scale: 0.85 }
            }
            transition={{
              opacity: { duration: 0.4, ease: 'easeOut' },
              scale: { type: 'spring', bounce: 0, visualDuration: 0.5 },
            }}
            style={{
              width: 80,
              height: 80,
              objectFit: 'contain',
              objectPosition: 'center',
              display: 'block',
              willChange: 'transform, opacity',
            }}
          />
        </Box>
      </Box>
      <Box
        component={motion.div}
        variants={dotsVariants}
        sx={{
          display: 'flex',
          gap: 1,
          justifyContent: 'center',
          willChange: 'opacity',
        }}
      >
        {[0, 1, 2].map((i) => (
          <Box
            key={i}
            sx={{
              width: 8,
              height: 8,
              borderRadius: '50%',
              bgcolor: (theme) => theme.palette.primary.main,
              animation: `${bounce} 1.4s ease-in-out ${i * 0.16}s infinite both`,
            }}
          />
        ))}
      </Box>
    </Box>
  );
}
